/**
 * SS-SEC-024 — Persistent banner shown while inside an explicit support-session
 * impersonation. Mounted ONCE via the pathless RootLayout in app/router.tsx so
 * it stays visible across every route (facility subroutes, /facilities picker,
 * /users, /support-session itself, login, etc.). RootLayout sits inside the
 * router tree (not App.tsx) because this banner uses useNavigate / useDispatch
 * which require their respective contexts.
 *
 * Visibility: rendered only when the authenticated user's JWT carries
 * impersonation claims (selectIsImpersonating === true). When the session
 * exits or expires, the user record loses the claims and the banner unmounts.
 *
 * UX rationale: an operator with cross-tenant write access MUST have a
 * constant, unmistakable visual signal that they are not in their own tenant.
 * The countdown also gives them awareness of the 30-min hard cap.
 */

import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import {
  selectCurrentUser,
  selectIsImpersonating,
  clearAuth,
} from '../../store/slices/authSlice';
import {
  exitSupportSession,
  getActiveSupportSession,
  type ActiveSupportSession,
} from '../../api/impersonationSession';
import type { AppDispatch } from '../../store';
import { operatorAppUrl } from '../../utils/operatorApp';

const REFRESH_EVERY_MS = 15_000;

function formatRemaining(msRemaining: number): string {
  if (msRemaining <= 0) return 'expired';
  const totalSec = Math.floor(msRemaining / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${String(sec).padStart(2, '0')}`;
}

export default function ImpersonationBanner() {
  const dispatch = useDispatch<AppDispatch>();
  const impersonating = useSelector(selectIsImpersonating);
  const user = useSelector(selectCurrentUser);
  const [session, setSession] = useState<ActiveSupportSession | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const [exiting, setExiting] = useState(false);

  // Poll the active-session endpoint every 15s so banner state reflects DB
  // reality (force-exits from another tab, natural expiry, etc.).
  useEffect(() => {
    if (!impersonating) {
      setSession(null);
      return;
    }
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const res = await getActiveSupportSession();
        if (cancelled) return;
        if (res.active) {
          setSession(res);
        } else {
          // Session ended out-of-band — force a local logout so the next
          // request doesn't 401 in the user's face.
          dispatch(clearAuth());
        }
      } catch {
        // Network blip — keep showing the last known session state; the
        // expiry tick below will eventually mark it expired if it really did.
      }
    };
    fetchOnce();
    const id = window.setInterval(fetchOnce, REFRESH_EVERY_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [impersonating, dispatch]);

  // 1-second tick for the countdown display.
  useEffect(() => {
    if (!impersonating) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [impersonating]);

  const handleExit = useCallback(async () => {
    if (exiting) return;
    setExiting(true);
    try {
      await exitSupportSession();
    } catch {
      // Even on failure, clear the local session — server-side row is the
      // source of truth, but the local cookie may have already expired.
    }
    // Force a hard reload to re-fetch /auth/me cleanly and refresh the user
    // record without the impersonation claims. Cheapest, most reliable way.
    window.location.href = '/facilities';
  }, [exiting]);

  if (!impersonating || !user) return null;

  const targetName = session?.targetOrganization.name ?? 'target tenant';
  const expiresAt = session?.expiresAt ? new Date(session.expiresAt).getTime() : null;
  const remaining = expiresAt !== null ? expiresAt - now : null;

  return (
    <Box
      role="alert"
      data-testid="impersonation-banner"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: (t) => t.zIndex.appBar + 1,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1,
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        borderBottom: '2px solid',
        borderColor: 'warning.dark',
      }}
    >
      <WarningIcon fontSize="small" />
      <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
        Impersonating <strong>{targetName}</strong> as {user.email}
        {remaining !== null && (
          <>
            {' - '}
            <span data-testid="impersonation-countdown">
              expires in {formatRemaining(remaining)}
            </span>
          </>
        )}
        {session?.reason && (
          <>
            {' - '}
            <em style={{ opacity: 0.9 }}>{session.reason}</em>
          </>
        )}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        disabled={exiting}
        onClick={handleExit}
        data-testid="impersonation-exit-btn"
        sx={{ borderColor: 'currentColor', whiteSpace: 'nowrap' }}
      >
        {exiting ? 'Exiting…' : 'Exit Impersonation'}
      </Button>
      <Button
        size="small"
        variant="text"
        color="inherit"
        onClick={() => window.location.assign(operatorAppUrl('/support-session'))}
        sx={{ whiteSpace: 'nowrap' }}
      >
        Switch
      </Button>
    </Box>
  );
}
